import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import InlineField from './InlineField';
import { operationName } from './agendaUtils';
import AgendaHoursCell from './AgendaHoursCell';
import StaffEditor from './StaffEditor';
import ConfirmDialog from '../../components/ConfirmDialog';
import { exportPersonnelPdf } from './accessListPdf';
import { formatTime, parseTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { canRead } from '../../utils/roles';
import haptic from '../../utils/haptic';

const KebabMenu = React.forwardRef(function KebabMenu({ items }, ref) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openAt(x, y) {
      setMenuPos({ top: Math.min(y, window.innerHeight - 220), right: Math.max(8, window.innerWidth - x) });
      setOpen(true);
    },
  }));

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!items.length) return null;

  function toggle(e) {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(o => !o);
  }

  return (
    <div className="kebab-wrap">
      <button ref={btnRef} className="kebab-btn" type="button" onClick={toggle} title="Más opciones">⋯</button>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}>
          <div ref={menuRef} className="kebab-menu">
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`kebab-item${item.danger ? ' danger' : ''}`}
                onClick={() => { setOpen(false); item.onClick(); }}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

export default function ArmadoRow({ armado, index, canAdmin, onLoad, onUpdateArmado, onRemoveArmado, isDragging, onDragStart, onDragEnd, canDrag, dragMime = 'application/x-depot-armado', personnel = [], assignmentModel = null, assignmentModelError = false, retryAssignmentModel = null, kbFocused = false, conflictKeys }) {
  const { user } = useAuth();
  const canExportPersonnelPdf = canRead(user, 'agenda.personal-pdf');
  const tabGroup = useRef([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);

  useEffect(() => {
    if (!kbFocused) return;
    function onEnter(e) {
      if (e.key === 'Enter') { e.preventDefault(); tabGroup.current[0]?.(); }
    }
    document.addEventListener('keydown', onEnter);
    return () => document.removeEventListener('keydown', onEnter);
  }, [kbFocused]);

  // Modo "Cambiar evento"
  const [reassigning, setReassigning]         = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [searchResults, setSearchResults]     = useState([]);
  const [reassignDropPos, setReassignDropPos] = useState(null);
  const [highlightIndex, setHighlightIndex]   = useState(0);
  const searchRef           = useRef(null);
  const reassignMenuRef     = useRef(null);
  const searchResultsRef    = useRef([]);

  // Auto-focus al abrir el modo reassign
  useEffect(() => {
    if (reassigning) setTimeout(() => searchRef.current?.focus(), 30);
  }, [reassigning]);

  // Calcular posición del dropdown cuando hay resultados en modo reassign; resetear highlight
  useEffect(() => {
    if (reassigning && searchResults.length > 0 && searchRef.current) {
      const rect = searchRef.current.getBoundingClientRect();
      setReassignDropPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    } else {
      setReassignDropPos(null);
    }
    setHighlightIndex(0);
  }, [reassigning, searchResults]);

  // Re-focus el input cuando aparece el dropdown (el portal mount no roba el foco, pero lo aseguramos)
  useEffect(() => {
    if (reassignDropPos) searchRef.current?.focus();
  }, [reassignDropPos]);

  // Click fuera cierra reassign; scroll cierra solo el dropdown (position:fixed no sigue el scroll)
  useEffect(() => {
    if (!reassigning) return;
    function onMouseDown(e) {
      if (e.target.closest('.armado-reassign')) return;
      if (reassignMenuRef.current?.contains(e.target)) return;
      setReassigning(false);
      setSearchQuery('');
      setSearchResults([]);
      searchResultsRef.current = [];
      setReassignDropPos(null);
    }
    function onScroll() { setSearchResults([]); searchResultsRef.current = []; setReassignDropPos(null); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [reassigning]);

  // Búsqueda debounced
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); searchResultsRef.current = []; return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/events/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(data);
        searchResultsRef.current = data;
      } catch { setSearchResults([]); searchResultsRef.current = []; }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  async function handleReassign(ev) {
    setReassigning(false);
    setSearchQuery('');
    setSearchResults([]);
    searchResultsRef.current = [];
    setReassignDropPos(null);
    try {
      const { data } = await api.put(`/armados/${armado.id}`, { event_id: ev.id });
      onUpdateArmado?.(data);
    } catch (e) {
      console.error(e);
      onLoad();
    }
  }

  async function saveField(field, val) {
    const apiField = armado.is_operation && field === 'notes' ? 'note' : field;
    try {
      if (armado.is_operation) {
        await api.patch(`/event-operations/${armado.id}`, { [apiField]: val || null });
      } else {
        await api.put(`/armados/${armado.id}`, { [field]: val || null });
      }
      onUpdateArmado?.(apiField === field ? { [field]: val || null } : { [field]: val || null, [apiField]: val || null });
    } catch {
      onLoad();
    }
  }

  async function handleDelete() {
    try {
      if (armado.is_operation) {
        await api.delete(`/event-operations/${armado.id}`);
      } else {
        await api.delete(`/armados/${armado.id}`);
      }
      onRemoveArmado?.(armado.id);
    } catch (e) { console.error(e); }
  }

  const menuItems = [];
  if (canExportPersonnelPdf) {
    menuItems.push({
      label: 'PDF Personal',
      onClick: () => exportPersonnelPdf({
        entityType: armado.is_operation ? 'operation' : 'armado',
        entityId: armado.id,
      }),
    });
  }
  if (canAdmin && !armado.is_operation) menuItems.push({ label: 'Cambiar evento', onClick: () => { setSearchQuery(armado.event_name || ''); setReassigning(true); } });
  if (canAdmin) menuItems.push({ label: 'Eliminar', onClick: () => setConfirmDelete(true), danger: true });

  const zebraClass    = index % 2 === 0 ? 'armado-odd' : 'armado-even';
  const draggingClass = isDragging ? ' is-dragging' : '';
  const focusClass    = kbFocused ? ' row-kb-focus' : '';
  // Misma derivación que la vista mobile: si se separan, los títulos derivan.
  const visibleTitle = operationName(armado)
    || (armado.type === 'desarmado' ? 'Desarmado' : 'Armado');

  const kebabRef = useRef(null);
  const menuTimer = useRef(null);
  const menuPressPos = useRef(null);
  const menuFired = useRef(false);
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  function toggleMob(e) {
    if (!isMobile()) return;
    if (menuFired.current) { menuFired.current = false; return; }
    if (e.target.closest('button, a, input, textarea, select, .se-line, .se-sheet, .se-role-pop, .kebab-wrap, .agenda-portal')) return;
    setMobOpen(o => !o);
  }
  function rowPointerDown(e) {
    if (!isMobile()) return;
    menuFired.current = false;
    if (e.target.closest('.inline-trigger.editable, button, a, input, textarea, select, .se-line, .se-sheet, .se-role-pop, .agenda-portal')) return;
    const x = e.clientX, y = e.clientY;
    menuPressPos.current = { x, y };
    clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => { menuFired.current = true; haptic.tick(); kebabRef.current?.openAt(x, y); }, 500);
  }
  function rowPointerMove(e) {
    if (!menuPressPos.current) return;
    if (Math.abs(e.clientX - menuPressPos.current.x) > 10 || Math.abs(e.clientY - menuPressPos.current.y) > 10) clearTimeout(menuTimer.current);
  }
  function rowPointerEnd() { clearTimeout(menuTimer.current); menuPressPos.current = null; }

  return (
    <div
      className={`armado-row ${zebraClass}${draggingClass}${focusClass}${mobOpen ? ' ag-mob-open' : ''}`}
      data-armado-id={armado.row_key || armado.id}
      data-armado-event={armado.event_id}
      onClick={toggleMob}
      onPointerDown={rowPointerDown}
      onPointerMove={rowPointerMove}
      onPointerUp={rowPointerEnd}
      onPointerLeave={rowPointerEnd}
    >
      {/* Col 1 — drag handle */}
      <div className="armado-cell armado-cell-order">
        <span
          className="ev-drag-handle"
          title="Reordenar"
          draggable={canDrag}
          onDragStart={canDrag ? e => {
            const rowEl = e.currentTarget.closest('[data-armado-id]');
            const cells = rowEl ? Array.from(rowEl.children) : [];
            const isLight = document.documentElement.dataset.theme === 'light';
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData(dragMime, '');
            e.dataTransfer.setData('text/plain', JSON.stringify({ id: armado.id, sourceDate: armado.armado_date?.slice(0, 10) }));
            if (cells.length) {
              const visual = [];
              for (const c of cells) {
                if (window.getComputedStyle(c).display === 'contents') {
                  for (const gc of c.children) visual.push(gc);
                } else {
                  visual.push(c);
                }
              }
              const rects = visual.map(c => c.getBoundingClientRect());
              const colWidths = rects.map(r => `${Math.round(r.width)}px`);
              const totalWidth = rects.reduce((s, r) => s + r.width, 0);
              const scopeWrap = document.createElement('div');
              if (isLight) scopeWrap.dataset.theme = 'light';
              scopeWrap.style.cssText = 'position:fixed;top:-9999px;left:0;pointer-events:none;';
              const scopePage = document.createElement('div');
              scopePage.className = 'agenda-page';
              const ghost = document.createElement('div');
              Object.assign(ghost.style, {
                display: 'grid',
                gridTemplateColumns: colWidths.join(' '),
                width: `${Math.round(totalWidth)}px`,
                borderRadius: '4px', overflow: 'hidden',
                opacity: '0.93', pointerEvents: 'none',
              });
              visual.forEach(cell => {
                const clone = cell.cloneNode(true);
                clone.style.background = window.getComputedStyle(cell).backgroundColor;
                ghost.appendChild(clone);
              });
              scopePage.appendChild(ghost);
              scopeWrap.appendChild(scopePage);
              document.body.appendChild(scopeWrap);
              e.dataTransfer.setDragImage(ghost, e.clientX - rects[0].left, e.clientY - rects[0].top);
              setTimeout(() => scopeWrap.parentNode?.removeChild(scopeWrap), 0);
            }
            onDragStart?.();
          } : undefined}
          onDragEnd={() => onDragEnd?.()}
        >≡</span>
      </div>

      {/* Col 2 — Nombre del evento + lugar editable */}
      <div className="armado-cell armado-cell-evento">
        {reassigning ? (
          <div className="armado-reassign">
            <input
              ref={searchRef}
              className="inline-input armado-search-input"
              placeholder="Buscar evento…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setReassigning(false); setSearchQuery(''); setSearchResults([]); searchResultsRef.current = []; setReassignDropPos(null);
                  return;
                }
                const len = searchResultsRef.current.length;
                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex(i => Math.min(i + 1, Math.max(0, len - 1))); return; }
                if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlightIndex(i => Math.max(i - 1, 0)); return; }
                if (e.key === 'Enter' && len > 0) { e.preventDefault(); handleReassign(searchResultsRef.current[highlightIndex]); }
              }}
            />
            {reassignDropPos && createPortal(
              <div
                className="agenda-portal"
                style={{ position: 'fixed', top: reassignDropPos.top, left: reassignDropPos.left, width: reassignDropPos.width, zIndex: 9999 }}
              >
              <div
                ref={reassignMenuRef}
                className="import-dropdown"
                onMouseDown={(e) => e.preventDefault()}
                style={{ margin: 0, fontSize: '13px', lineHeight: '1.4' }}
              >
                {searchResults.map((r, i) => (
                  <div key={r.id} className={`import-option${i === highlightIndex ? ' highlighted' : ''}`} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleReassign(r); }}>
                    <div style={{ fontWeight: 700 }}>{r.name}</div>
                    <div style={{ color: 'var(--ag-muted)', fontSize: 11 }}>
                      {r.event_date?.slice(0, 10)}{r.place ? ` · ${r.place}` : ''}
                    </div>
                  </div>
                ))}
              </div>
              </div>,
              document.body
            )}
          </div>
        ) : (
          <>
            <div
              className="armado-evento-name"
            >
              {visibleTitle}
            </div>
            {(armado.place || armado.event_place) && (
              <div className="armado-evento-place" style={{ color: 'var(--ag-dim)', fontSize: 12 }}>
                {armado.place || armado.event_place}
              </div>
            )}
          </>
        )}
      </div>

      <AgendaHoursCell
        className="armado-cell armado-cell-hours"
        rows={[
          { key: 'start', label: 'INI', value: formatTime(armado.start_time), onSave: v => saveField('start_time', parseTime(v)), canEdit: canAdmin, tabGroup, tabIndex: 0 },
          { key: 'end', label: 'FIN', value: formatTime(armado.end_time), onSave: v => saveField('end_time', parseTime(v)), canEdit: canAdmin, tabGroup, tabIndex: 1 },
        ]}
      />

      {/* Col 4 — Armado (vacía: la cuadrilla va en Operación) */}
      <div className="armado-cell" style={{ gridColumn: '4 / 5' }} />

      {/* Col 5 — Personal asignado (Operación) */}
      <div className="armado-cell armado-cell-staff" style={{ gridColumn: '5 / 6' }}>
        <StaffEditor
          armadoId={armado.is_operation ? null : armado.id}
          operationId={armado.is_operation ? armado.id : null}
          initialAssignments={armado.staff_assignments || []}
          initialSections={armado.staff_sections || []}
          assignmentModel={assignmentModel}
          assignmentModelError={assignmentModelError}
          retryAssignmentModel={retryAssignmentModel}
          personnel={personnel}
          canEdit={canAdmin}
          entityDate={(armado.armado_date || armado.operation_date)?.slice(0, 10)}
          startTime={armado.start_time?.slice(0, 5)}
          endTime={armado.end_time?.slice(0, 5)}
          externalConflictKeys={conflictKeys}
        />
      </div>

      {/* Col 6 — Desarmado (vacía) */}
      <div className="armado-cell" style={{ gridColumn: '6 / 7' }} />

      {/* Col 7 — Notas */}
      <div
        className="armado-cell armado-cell-notes"
        onClick={canAdmin ? () => tabGroup.current[3]?.() : undefined}
        style={{ gridColumn: '7 / 8', cursor: canAdmin ? 'text' : undefined }}
      >
        <InlineField
          value={armado.notes || ''}
          onSave={v => saveField('notes', v)}
          canEdit={canAdmin}
          multiline
          tabGroup={tabGroup}
          tabIndex={3}
          placeholder=""
        />
      </div>

      {/* Col 8 — Acciones */}
      <div className="armado-cell armado-cell-actions" style={{ gridColumn: '8 / 9' }}>
        <KebabMenu ref={kebabRef} items={menuItems} />
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Eliminar armado"
          message={`¿Eliminar el ${armado.type === 'desarmado' ? 'desarmado' : 'armado'}${armado.event_name ? ` de "${armado.event_name}"` : ''}? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={() => { setConfirmDelete(false); handleDelete(); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
